import {
  type AreaCreate,
  AreaSchema,
  type AreaUpdate,
  type CalendarSettingsCreate,
  CalendarSettingsSchema,
  type HolidayCreate,
  HolidaySchema,
  paginatedResponseSchema,
  type PermissionTypeCreate,
  PermissionTypeSchema,
  type PermissionTypeUpdate,
} from '@gestion-permisos/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { useApi } from '../api/api-context.js';
import { ifMatch } from '../api/intent.js';
import { ApiProblem } from '../api/problem.js';

/** Tamaño de página de los catálogos de administración. */
export const ADMIN_PAGE_SIZE = 20;

const AreaPageSchema = paginatedResponseSchema(AreaSchema);
const PermissionTypePageSchema = paginatedResponseSchema(PermissionTypeSchema);
const HolidayListSchema = z.array(HolidaySchema);

const isStale = (error: unknown) => error instanceof ApiProblem && error.status === 412;

// ---------------------------------------------------------------- Áreas

export function useAreasPage(page: number) {
  const api = useApi();
  return useQuery({
    queryKey: ['admin', 'areas', page],
    queryFn: () => api.get(`/api/v1/areas?page=${page}&pageSize=${ADMIN_PAGE_SIZE}`, AreaPageSchema),
  });
}

/** `POST /areas` (sin `Idempotency-Key`: la API no la pide en los catálogos). */
export function useCreateArea() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: AreaCreate) => api.mutate('POST', '/api/v1/areas', body, { schema: AreaSchema }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admin', 'areas'] }),
  });
}

/** `PATCH /areas/{id}` con `If-Match` y solo los campos cambiados. Ante un 412, ver `useRereadArea`. */
export function useUpdateArea() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, version, changes }: { id: string; version: number; changes: AreaUpdate }) =>
      api.mutate('PATCH', `/api/v1/areas/${encodeURIComponent(id)}`, changes, { schema: AreaSchema, headers: { 'If-Match': ifMatch(version) } }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admin', 'areas'] }),
  });
}

/** Tras un 412: vuelve a leer el área (`GET /areas/{id}`) y el listado, para recargar el formulario. */
export function useRereadArea() {
  const api = useApi();
  const queryClient = useQueryClient();
  return async (id: string) => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'areas'] });
    return api.get(`/api/v1/areas/${encodeURIComponent(id)}`, AreaSchema);
  };
}

// ---------------------------------------------------------------- Tipos de permiso

/** Todos los tipos (también los inactivos): el administrador no filtra por `active`. */
export function useAdminTypesPage(page: number) {
  const api = useApi();
  return useQuery({
    queryKey: ['admin', 'permission-types', page],
    queryFn: () => api.get(`/api/v1/permission-types?page=${page}&pageSize=${ADMIN_PAGE_SIZE}`, PermissionTypePageSchema),
  });
}

function useAfterTypeChange() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'permission-types'] });
    // El catálogo de tipos activos del formulario de solicitud y de la analítica.
    void queryClient.invalidateQueries({ queryKey: ['catalog', 'permission-types'] });
  };
}

export function useCreateType() {
  const api = useApi();
  const after = useAfterTypeChange();
  return useMutation({
    mutationFn: (body: PermissionTypeCreate) => api.mutate('POST', '/api/v1/permission-types', body, { schema: PermissionTypeSchema }),
    onSuccess: after,
  });
}

/** `PATCH /permission-types/{id}` con `If-Match`. No hay `GET` del tipo: un 412 vuelve a leer el listado. */
export function useUpdateType() {
  const api = useApi();
  const after = useAfterTypeChange();
  return useMutation({
    mutationFn: ({ id, version, changes }: { id: string; version: number; changes: PermissionTypeUpdate }) =>
      api.mutate('PATCH', `/api/v1/permission-types/${encodeURIComponent(id)}`, changes, {
        schema: PermissionTypeSchema,
        headers: { 'If-Match': ifMatch(version) },
      }),
    onSuccess: after,
    onError: (error) => {
      if (isStale(error)) after();
    },
  });
}

// ---------------------------------------------------------------- Calendario y festivos

/** Configuración vigente (la misma consulta que el formulario de solicitud). */
export function useCurrentCalendarSettings() {
  const api = useApi();
  return useQuery({
    queryKey: ['catalog', 'calendar-settings'],
    queryFn: () => api.get('/api/v1/calendar/settings', CalendarSettingsSchema),
  });
}

/** `POST /calendar/settings`: vigencia de hoy en adelante. Si es anterior, la API responde 422 (no se valida aquí). */
export function useCreateCalendarSettings() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CalendarSettingsCreate) => api.mutate('POST', '/api/v1/calendar/settings', body, { schema: CalendarSettingsSchema }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['catalog', 'calendar-settings'] }),
  });
}

/** Festivos de un año (`YYYY`, como texto). Los calcula y ajusta la API; el cliente no calcula ninguno. */
export function useHolidayYear(year: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ['catalog', 'holidays', year],
    queryFn: () => api.get(`/api/v1/calendar/holidays?year=${year}`, HolidayListSchema),
    enabled: year !== undefined,
  });
}

function useAfterHolidayChange() {
  const queryClient = useQueryClient();
  return () => void queryClient.invalidateQueries({ queryKey: ['catalog', 'holidays'] });
}

export function useCreateHoliday() {
  const api = useApi();
  const after = useAfterHolidayChange();
  return useMutation({
    mutationFn: (body: HolidayCreate) => api.mutate('POST', '/api/v1/calendar/holidays', body, { schema: HolidaySchema }),
    onSuccess: after,
  });
}

/** `POST /calendar/holidays/{fecha}/disable` o `/enable`, con la fecha civil en la ruta, como texto. */
export function useToggleHoliday() {
  const api = useApi();
  const after = useAfterHolidayChange();
  return useMutation({
    mutationFn: ({ date, action }: { date: string; action: 'disable' | 'enable' }) =>
      api.mutate('POST', `/api/v1/calendar/holidays/${encodeURIComponent(date)}/${action}`, undefined, { schema: HolidaySchema }),
    onSuccess: after,
  });
}
